import type { UserRow } from '@exam-management/user-management';
import { createContext, useMemo, useState, type PropsWithChildren } from 'react';

import { AddUsersToQBankStructure, EditUserContentVisibility } from '../../containers';

import type { QBankStructureCVModalContextData } from './types';

export enum QBankStructureCVModalAction {
  ADD_MEMBER_TO_QBANK_STRUCTURE = 'ADD_MEMBER_TO_QBANK_STRUCTURE',
  EDIT_CONTENT_VISIBILITY_OF_MEMBER = 'EDIT_CONTENT_VISIBILITY_OF_MEMBER',
}

const QBankStructureCVModalContext = createContext<QBankStructureCVModalContextData | undefined>(
  undefined,
);

type QBankStructureCVModalProviderProps = PropsWithChildren;

function QBankStructureCVModalProvider(props: Readonly<QBankStructureCVModalProviderProps>) {
  const { children } = props;
  const [action, setAction] = useState<QBankStructureCVModalAction | null>(null);
  const [editUserCVDetails, setEditUserCVDetails] = useState<UserRow | null>(null);

  const handleSetAction = (action: QBankStructureCVModalAction | null) => {
    setAction(action);
  };

  const handleSetEditUserCVDetails = (data: UserRow) => {
    setEditUserCVDetails(data);
  };

  const handleClose = () => {
    setAction(null);
  };

  const contextValue = useMemo(() => {
    return {
      setAction: handleSetAction,
      setEditUserCVDetails: handleSetEditUserCVDetails,
    };
  }, []);

  const isOpenAddMemberToQBankStructureModal =
    action === QBankStructureCVModalAction.ADD_MEMBER_TO_QBANK_STRUCTURE;
  const isOpenEditUserCVDetailsModal =
    action === QBankStructureCVModalAction.EDIT_CONTENT_VISIBILITY_OF_MEMBER && !!editUserCVDetails;

  return (
    <QBankStructureCVModalContext.Provider value={contextValue}>
      {children}
      {isOpenAddMemberToQBankStructureModal ? (
        <AddUsersToQBankStructure
          open={isOpenAddMemberToQBankStructureModal}
          onClose={handleClose}
        />
      ) : null}
      {isOpenEditUserCVDetailsModal ? (
        <EditUserContentVisibility
          open={isOpenEditUserCVDetailsModal}
          onClose={handleClose}
          userDetails={editUserCVDetails}
        />
      ) : null}
    </QBankStructureCVModalContext.Provider>
  );
}
export { QBankStructureCVModalContext, QBankStructureCVModalProvider };
